import { Interpolation, Position } from './geodesy-types';
const EarthRadiusNm = 3443.92; // [NM];

/**
 * Calculates the distance between two points on the Earth's surface using the Haversine formula
 * @param p1    - The first point [lon, lat]
 * @param p2    - The second point [lon, lat]
 * @returns     - The distance between the two points [NM]
 */
function calculateDistance(p1: Position, p2: Position): number {
    const lat1 = (p1[1] * Math.PI) / 180;
    const lat2 = (p2[1] * Math.PI) / 180;
    const deltaLat = ((p2[1] - p1[1]) * Math.PI) / 180;
    const deltaLng = ((p2[0] - p1[0]) * Math.PI) / 180;

    const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EarthRadiusNm * c; // Distance in NM
}

/**
 * Adds points along a route at a fixed distance.
 * @param positions - The route waypoints [lon, lat]
 * @param distance  - The distance between the interpolated points [NM]
 * @param interpolationMethod   - The interpolation method to use. 'greatCircle' or 'linear', default is 'linear'
 * @returns The route with additional points interpolated along it.
 */
function addPointsAlongRoute(positions: Position[], distance: number, interpolationMethod?: Interpolation): Position[] {
    const interpolation = getInterpolation(interpolationMethod || 'linear');
    const newPositions: Position[] = [];
    for (let i = 0; i < positions.length - 1; i++) {
        newPositions.push(positions[i]);
        const interpolatedPoints = interpolation(positions[i], positions[i + 1], distance);
        newPositions.push(...interpolatedPoints);
    }
    newPositions.push(positions[positions.length - 1]);
    return newPositions;
}

function interpolateGreatCircle(p1: Position, p2: Position, distance: number): Position[] {
    const segmentDistance = calculateDistance(p1, p2);
    const numPoints = Math.floor(segmentDistance / distance);
    const points: Position[] = [];

    const lat1 = (p1[1] * Math.PI) / 180;
    const lng1 = (p1[0] * Math.PI) / 180;
    const lat2 = (p2[1] * Math.PI) / 180;
    const lng2 = (p2[0] * Math.PI) / 180;

    for (let i = 1; i <= numPoints; i++) {
        const f = i / (numPoints + 1);
        const A = Math.sin((1 - f) * segmentDistance) / Math.sin(segmentDistance);
        const B = Math.sin(f * segmentDistance) / Math.sin(segmentDistance);
        const x = A * Math.cos(lat1) * Math.cos(lng1) + B * Math.cos(lat2) * Math.cos(lng2);
        const y = A * Math.cos(lat1) * Math.sin(lng1) + B * Math.cos(lat2) * Math.sin(lng2);
        const z = A * Math.sin(lat1) + B * Math.sin(lat2);

        const lat = (Math.atan2(z, Math.sqrt(x * x + y * y)) * 180) / Math.PI;
        const lng = (Math.atan2(y, x) * 180) / Math.PI;
        points.push([lng, lat]);
    }

    return points;
}

function interpolatePointsByDistance(p1: Position, p2: Position, distance: number): Position[] {
    const segmentDistance = calculateDistance(p1, p2);
    const numPoints = Math.floor(segmentDistance / distance);
    const points: Position[] = [];

    for (let i = 1; i <= numPoints; i++) {
        const t = i / (numPoints + 1);
        const lat = p1[1] + t * (p2[1] - p1[1]);

        const lng = getLongitude180(p1[0], p2[0], t);
        points.push([lng, lat]);
    }

    return points;
}

function getLongitude180(x: number, y: number, f: number): number {
    if (Math.abs(x - y) > 180) {
        if (x > 0) {
            const d = x + (360 + y - x) * f;
            return d > 180 ? d - 360 : d;
        } else {
            const d = x - (360 - y + x) * f;
            return d < -180 ? d + 360 : d;
        }
    }
    return x + (y - x) * f;
}

function getInterpolation(interpolation: Interpolation): (p1: Position, p2: Position, distance: number) => Position[] {
    switch (interpolation) {
        case 'linear':
            return interpolatePointsByDistance;
        case 'greatCircle':
            return interpolateGreatCircle;
        default:
            throw new Error('Unsupported interpolation method');
    }
}
export default { calculateDistance, addPointsAlongRoute, getLongitude180 };
